import React, { useState, useEffect } from 'react'
import { httpClient } from "../../funcs"
import { notifyAboutError } from "../../methods/notifyUser"
import { formatDateTime } from "../../methods/formatDate"
import { ListLoader } from "../Contracts/ContractList/PageLoader"
import { ErrorPageView } from "../../components/ErrorPageView"
import { getServiceDisplayName } from "../../types/ServiceTypes"
import { applyDiscountToPrice } from "../../util/priceCalculation"
import TransactionItem, { TransactionItemData } from './TransactionItem'
import "pages/Styles/TransactionHistory.sass"

interface Transaction {
    _id: string
    created_at: string
    amount: number
    type: 'package' | 'subscription' | 'cart'
    status: string
    payment_method_id?: string
    payment_method?: string
    tariff_info?: {
        name: string
        prepaid_documents_count: number
        period: string
        tariff_upgrades?: string[]
    }
    cart_data?: {
        items: Array<{
            _id: string
            name: string
            type: string
            price: number
            quantity: number
            discount?: number
            prepaid_documents_count?: number
            remaining_documents?: number
            period?: string
            upgrade_additional_price?: { [key: string]: number }
            tariff_upgrades?: string[]
            description?: string
            icon?: string
            legal_checks_balance?: number
        }>
    }
}

interface TransactionHistoryProps {
    $store: any
    $commitToStore: (data: any) => void
}

const TransactionHistory: React.FC<TransactionHistoryProps> = ({ $store, $commitToStore }) => {
    const [transactions, setTransactions] = useState<Transaction[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<any>(null)

    useEffect(() => {
        loadTransactions()
    }, [])

    async function loadTransactions() {
        try {
            setLoading(true)
            setError(null)
            const { data } = await httpClient.get("/transactions-history")
            setTransactions(data.transactions || [])
        } catch (error) {
            console.error("Ошибка при загрузке истории транзакций:", error)
            setError(error)
            notifyAboutError({ $commitToStore }, error)
        } finally {
            setLoading(false)
        }
    }

    function formatDate(dateString: string): string {
        return formatDateTime(dateString)
    }

    function formatAmount(amount: number): string {
        return new Intl.NumberFormat('ru-RU', {
            style: 'currency',
            currency: 'RUB',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount / 100)
    }

    function getPaymentMethodText(transaction: Transaction): string {
        // Если есть payment_method_id - это точно карта
        if (transaction.payment_method_id) {
            return 'Карта'
        }
        
        // Если есть поле payment_method, используем его
        if (transaction.payment_method) {
            return transaction.payment_method === 'card' ? 'Карта' : 'Счет'
        }
        
        // По умолчанию считаем счетом
        return 'Счет'
    }



    function getValidUntilDate(createdAt: string, period?: string): string | null {
        if (!period) {
            return null
        }

        const createdDate = new Date(createdAt)
        let validUntilDate: Date

        if (period === 'year') {
            // Добавляем год
            validUntilDate = new Date(createdDate)
            validUntilDate.setFullYear(validUntilDate.getFullYear() + 1)
        } else if (period === 'month') {
            // Добавляем месяц
            validUntilDate = new Date(createdDate)
            validUntilDate.setMonth(validUntilDate.getMonth() + 1)
        } else {
            return null
        }

        // Форматируем дату в российском формате
        return validUntilDate.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        })
    }

    function mapTransactionToItems(transaction: Transaction): TransactionItemData[] {
        const items: TransactionItemData[] = []

        // 1. Основной тариф (package_v2, package, subscription, pro_subscription)
        if (transaction.type === 'cart' && transaction.cart_data?.items) {
            const packageItem = transaction.cart_data.items.find(item => item.type === 'package_v2')
            const proItem = transaction.cart_data.items.find(item => item.type === 'pro_subscription')

            if (packageItem) {
                const title = 'Пакет: ' + packageItem.name
                let subtitle = ''
                
                if (packageItem.prepaid_documents_count === -1) {
                    subtitle = 'Безлимит'
                } else {
                    subtitle = `${packageItem.prepaid_documents_count} документов`
                }
                
                const validUntil = getValidUntilDate(transaction.created_at, packageItem.period)
                if (validUntil) {
                    subtitle += ` • действует до ${validUntil}`
                }
                
                if (packageItem.remaining_documents !== undefined && packageItem.prepaid_documents_count !== -1) {
                    subtitle += ` • ${packageItem.remaining_documents} осталось`
                }
                
                items.push({
                    title,
                    subtitle,
                    amount: packageItem.price,
                    isMain: true
                })
            } else if (proItem) {
                const validUntil = getValidUntilDate(transaction.created_at, proItem.period)
                let subtitle = 'Брендирование страницы подписания · пакет документов не включён'
                if (validUntil) {
                    subtitle += ` • действует до ${validUntil}`
                }

                items.push({
                    title: 'Подписка Pro: ' + proItem.name,
                    subtitle,
                    amount: applyDiscountToPrice(proItem.price, proItem.discount || 0),
                    isMain: true
                })
            }
        } else if (transaction.tariff_info) {
            const isSubscription = transaction.tariff_info.period === 'year' || transaction.tariff_info.period === 'month'
            const title = (isSubscription ? 'Подписка: ' : 'Пакет: ') + transaction.tariff_info.name
            let subtitle = ''
            
            if (transaction.tariff_info.prepaid_documents_count === -1) {
                subtitle = 'Безлимит'
            } else {
                subtitle = `${transaction.tariff_info.prepaid_documents_count} документов`
            }
            
            const validUntil = getValidUntilDate(transaction.created_at, transaction.tariff_info.period)
            if (validUntil) {
                subtitle += ` • действует до ${validUntil}`
            }
            
            items.push({
                title,
                subtitle,
                amount: transaction.amount,
                isMain: true
            })
        } else if (transaction.type === 'package') {
            items.push({
                title: 'Пакет документов',
                amount: transaction.amount,
                isMain: true
            })
        } else if (transaction.type === 'subscription') {
            items.push({
                title: 'Подписка',
                amount: transaction.amount,
                isMain: true
            })
        } else {
            // Для других типов (например, template_configuration)
            items.push({
                title: getServiceDisplayName(transaction.type),
                amount: transaction.amount,
                isMain: true
            })
        }

        // 2. Апгрейды и дополнительные услуги
        if (transaction.type === 'cart' && transaction.cart_data?.items) {
            const packageItem = transaction.cart_data.items.find(item => item.type === 'package_v2')
            const proItem = transaction.cart_data.items.find(item => item.type === 'pro_subscription')
            
            // Дополнительные услуги (не пакеты; pro как main уже показан выше)
            const additionalServices = transaction.cart_data.items.filter(item => {
                if (item.type === 'package_v2') {
                    return false
                }
                if (item.type === 'pro_subscription' && !packageItem) {
                    return false
                }
                return true
            })
            additionalServices.forEach(item => {
                const subtitle = item.type === 'pro_subscription'
                    ? 'Брендирование · пакет документов не включён'
                    : undefined

                items.push({
                    title: item.name,
                    subtitle,
                    amount: item.type === 'pro_subscription'
                        ? applyDiscountToPrice(item.price, item.discount || 0)
                        : item.price
                })
            })
            
            // Апгрейды из package_v2
            if (packageItem) {
                const upgrades = packageItem.tariff_upgrades || []
                const upgradePrices = packageItem.upgrade_additional_price || {}
                
                upgrades.forEach(upgradeType => {
                    const upgradePrice = upgradePrices[upgradeType]
                    if (upgradePrice) {
                        items.push({
                            title: getServiceDisplayName(upgradeType),
                            amount: upgradePrice
                        })
                    }
                })
            }
        } else if (transaction.tariff_info?.tariff_upgrades) {
            // Апгрейды из tariff_info (если будут в будущем)
            const upgrades = transaction.tariff_info.tariff_upgrades
            upgrades.forEach(upgradeType => {
                items.push({
                    title: getServiceDisplayName(upgradeType),
                    amount: 0 // Цена апгрейда не доступна в tariff_info
                })
            })
        }

        return items
    }


    function renderTransaction(transaction: Transaction) {
        const items = mapTransactionToItems(transaction)
        const hasMultipleItems = items.length > 1

        return (
            <div key={transaction._id} className="transaction-card">
                <div className="transaction-main-content">
                    {/* Левая часть - дата и способ оплаты */}
                    <div className="transaction-left">
                        <div className="transaction-date">
                            Дата покупки:<br/>{formatDate(transaction.created_at)}
                        </div>
                        <div className="transaction-payment-method">
                            {getPaymentMethodText(transaction)}
                        </div>
                    </div>

                    {/* Вертикальная черта (только на десктопе) */}
                    <div className="transaction-divider"></div>

                    {/* Правая часть - информация о заказе */}
                    <div className="transaction-right">
                        <div className="transaction-items">
                            {items.map((item, index) => (
                                <TransactionItem key={index} item={item} />
                            ))}
                        </div>
                        {hasMultipleItems && (
                            <div className="transaction-total">
                                <div className="transaction-total-title">Итого:</div>
                                <div className="transaction-total-amount">
                                    {formatAmount(transaction.amount)}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <ErrorPageView 
                error={error}
                $commitToStore={$commitToStore}
                customAction={{
                    text: 'Попробовать снова',
                    onClick: loadTransactions
                }}
            />
        )
    }

    return (
        <div className="transaction-history">
            { loading && <ListLoader loading={loading} />}
            {!loading && transactions.length === 0 && (
                <div className="empty-state">
                    <div className="empty-state-title">История пуста</div>
                    <div className="empty-state-description">
                        Здесь будут отображаться ваши успешные транзакции
                    </div>
                </div>
            )}
            {!loading && transactions.length > 0 && (
                <div className="transaction-list">
                    {transactions.map(renderTransaction)}
                </div>
            )}
        </div>
    )
}

export default TransactionHistory
